/* eslint-disable react/no-danger */
import { useState, useEffect, lazy, Suspense } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import uniqid from 'uniqid';
import { BounceLoader, TabNav, TextBoxContent } from '@bwoty-web/styleguide';
import SectionParagraph from '../../../common/sectionParagraph';
import AccommodationConceptContent from './accommodation/concept/accommodationConceptContent';
import MemoizedAlternativeContentTabWrapper from './alternativeContent/alternativeContentTabWrapper';
import { updateUrlWithOpenHotelSection } from '../../../utils/url';
import { sectionKeys } from './helper';
import AccommodationContent from './accommodation/accommodationContent';
import './sectionContentContainer.scss';

const WeatherChart = lazy(() =>
  import(
    /* webpackChunkName: "weather-chart" */ '../../../common/weather/weatherChart'
  ),
);
const MapContentResort = lazy(() =>
  import(
    /* webpackChunkName: "map-content-resort" */ '../../facts/map/MapContentResort'
  ),
);
const SustainabilityContent = lazy(() =>
  import(
    /* webpackChunkName: "sustainability" */ './sustainability/sustainabilityContent'
  ),
);
const MemoizedHotelReviewContent = lazy(() =>
  import(/* webpackChunkName: "hotel-reviews" */ './hotelReviews/hotelReviews'),
);

const SectionContentContainer = ({
  section,
  siteSettings,
  wvId,
  toggleRoomInfo,
  isActive,
  selectedSubSection,
  changeSelectedSection,
  nonConceptAccommodation,
}) => {
  const tabOptions = section?.content?.map((contentPart) => {
    return contentPart.heading;
  });

  const [selectedOption, setSelectionOption] = useState(selectedSubSection);

  useEffect(() => {
    setSelectionOption(selectedSubSection);
  }, [selectedSubSection]);

  const getAccommodationContent = () => {
    const { content, alternativeContentExists, bodyArray, labels } = section;
    const selectedContent = content[selectedOption];

    // Do we ever have alternativeContent for accommodation?
    if (alternativeContentExists) {
      if (typeof window !== 'undefined' && window) {
        const partialPageElement = document.getElementById(
          `hotel-sections-view-partial-page-${section.key}`,
        );
        if (partialPageElement) {
          return (
            <div
              dangerouslySetInnerHTML={{ __html: partialPageElement.innerHTML }}
            />
          );
        }
        return null;
      }
    }

    // Concept hotels
    if (bodyArray && bodyArray.length > 0) {
      return (
        <AccommodationConceptContent
          conceptContent={bodyArray}
          labels={labels}
          toggleRoomInfo={toggleRoomInfo}
          selectedSubSection={selectedSubSection}
        />
      );
    }

    // Hotels with room type content
    if (
      nonConceptAccommodation?.roomTypes?.length > 0 &&
      nonConceptAccommodation.roomTypes.some(
        (i) => i.accommodationSupplier === 'own',
      )
    ) {
      return (
        <AccommodationContent
          nonConceptAccommodation={nonConceptAccommodation}
          siteSettings={siteSettings}
          labels={labels}
          toggleRoomInfo={toggleRoomInfo}
          accommodationSection={section}
        />
      );
    }

    // Other hotels with body content or paragraphs
    const renderParagraphs = (paragraphs, sectionIdx = 0, heading = '') =>
      paragraphs?.map((paragraph, idx) => (
        <SectionParagraph
          key={uniqid('section-paragraph-')}
          paragraph={paragraph}
          {...(!(sectionIdx === 0 || idx !== 0) && { sectionHeading: heading })}
        />
      ));

    if (selectedContent?.body) {
      return (
        <div>
          {selectedContent.body.map(({ heading, paragraphs }, sectionIdx) =>
            renderParagraphs(paragraphs, sectionIdx, heading),
          )}
        </div>
      );
    }

    return <div>{renderParagraphs(selectedContent?.paragraphs)}</div>;
  };

  const getWeatherContent = () => {
    const { labels, climate } = section;
    return (
      <Suspense fallback={<BounceLoader />}>
        <WeatherChart
          climate={climate}
          siteId={siteSettings?.siteId}
          currentMonth={new Date().getMonth()}
          texts={{
            waterTempText: labels?.waterTempText,
            sunHoursPerDayText: labels?.sunHoursPerDayText,
            rainFreeDaysText: labels?.rainFreeDaysText,
            explanationText: labels?.climateExplanation,
          }}
        />
      </Suspense>
    );
  };

  const getMapContent = () => {
    const { labels } = section;
    const resortId = section?.geographical?.resort?.id;
    return (
      <Suspense fallback={<BounceLoader />}>
        <MapContentResort
          siteId={siteSettings?.siteId}
          resortId={resortId}
          selectedMarkerWvId={wvId}
          isExpanded={isActive}
          texts={{
            tripAdvisor: labels?.tripAdvisor,
            guestRating: labels?.guestRating,
            button: labels?.button,
            noPriceLabel: labels?.noPriceLabel,
            adultFriendly: labels?.adultFriendly,
            childFriendly: labels?.childFriendly,
          }}
          baseUrl={siteSettings?.baseUrl}
          imageDomain={siteSettings?.imageDomain}
          close={() => changeSelectedSection(sectionKeys.overview)}
        />
      </Suspense>
    );
  };

  const getRatingContent = () => {
    const { reviews, ratings, labels } = section;
    return (
      <Suspense fallback={<BounceLoader />}>
        <MemoizedHotelReviewContent
          ratings={ratings}
          reviews={reviews}
          texts={labels}
          selectedSubSection={selectedSubSection}
          changeSelectedSection={changeSelectedSection}
        />
      </Suspense>
    );
  };

  const getSustainabilityContent = () => {
    const { sustainability, productType } = section;
    return (
      <Suspense fallback={<BounceLoader />}>
        <SustainabilityContent
          sustainability={sustainability}
          productType={productType?.toLowerCase()}
        />
      </Suspense>
    );
  };

  const getDefaultContent = () => {
    const { alternativeContentExists, labels } = section;
    const content = section.content[selectedOption];
    if (alternativeContentExists) {
      if (typeof window !== 'undefined' && window) {
        const partialPageElement = document.getElementById(
          `hotel-sections-view-partial-page-${section.key}`,
        );
        if (!partialPageElement) {
          const partialPageTabs = document.getElementById(
            `hotel-sections-view-partial-tabpages-${section.key}`,
          );
          if (partialPageTabs) {
            const partialPageTabsElements = [
              ...partialPageTabs.querySelectorAll('[class*="tabpage"]'),
            ];
            const alternativeContent = [];
            partialPageTabsElements.forEach((partialPageTabElement) => {
              alternativeContent.push({
                name: partialPageTabElement.id.replace(
                  'hotel-sections-view-partial-tabpage-',
                  '',
                ),
                html: partialPageTabElement.innerHTML,
              });
            });
            return (
              <MemoizedAlternativeContentTabWrapper
                sectionKey={section.key}
                alternativeContent={alternativeContent}
                labels={labels}
                selectedSubSection={selectedSubSection}
              />
            );
          }
        }
        if (partialPageElement) {
          return (
            <div
              dangerouslySetInnerHTML={{ __html: partialPageElement.innerHTML }}
            />
          );
        }
      }
      return null;
    }

    return (
      <div>
        {content?.paragraphs?.map((paragraph) => {
          return (
            <SectionParagraph
              paragraph={paragraph}
              key={uniqid('section-paragraph-')}
            />
          );
        })}
      </div>
    );
  };

  const getContent = () => {
    const nonInitRenderSections = [
      'weather',
      'map',
      'rating',
      'sustainability',
    ];

    // bow out early for pushes not needed for SEO unless they are expanded
    if (nonInitRenderSections.includes(section.key) && !isActive) {
      return null;
    }

    switch (section.key) {
      case 'accommodation': {
        return (
          <TextBoxContent noMaxWidth>
            {getAccommodationContent()}
          </TextBoxContent>
        );
      }
      case 'weather': {
        return getWeatherContent();
      }
      case 'map': {
        return getMapContent();
      }
      case 'rating': {
        return getRatingContent();
      }
      case 'sustainability': {
        return getSustainabilityContent();
      }
      default: {
        return (
          <TextBoxContent noMaxWidth>{getDefaultContent()}</TextBoxContent>
        );
      }
    }
  };

  const content = getContent();

  if (!content) {
    return null;
  }

  return (
    <div
      className={cx(
        'gw-section-content',
        `gw-section-content--${section.key}`,
        {
          'gw-section-content--hidden': !isActive,
        },
      )}
    >
      <h2>{section.heading}</h2>
      {tabOptions?.length > 1 && (
        <TabNav
          tabs={tabOptions}
          selected={selectedOption}
          onClick={(index) => {
            updateUrlWithOpenHotelSection(`${section.key}:${index}`);
            setSelectionOption(index);
          }}
        />
      )}
      {content}
    </div>
  );
};

SectionContentContainer.propTypes = {
  section: PropTypes.shape({
    heading: PropTypes.string,
    key: PropTypes.string,
    content: PropTypes.arrayOf(
      PropTypes.shape({
        body: PropTypes.arrayOf(PropTypes.shape({})),
        paragraphs: PropTypes.arrayOf(PropTypes.shape({})),
      }),
    ),
    labels: PropTypes.shape({
      waterTempText: PropTypes.string,
      sunHoursPerDayText: PropTypes.string,
      rainFreeDaysText: PropTypes.string,
      tripAdvisor: PropTypes.string,
      guestRating: PropTypes.string,
      button: PropTypes.string,
      noPriceLabel: PropTypes.string,
      adultFriendly: PropTypes.string,
      childFriendly: PropTypes.string,
      climateExplanation: PropTypes.string,
    }),
    alternativeContentExists: PropTypes.bool,
    climate: PropTypes.shape({}),
    name: PropTypes.string,
    meta: PropTypes.shape({
      url: PropTypes.string,
    }),
    geographical: PropTypes.shape({
      resort: PropTypes.shape({
        id: PropTypes.number,
      }),
    }),
    ratings: PropTypes.shape({}),
    sustainability: PropTypes.shape({}),
    reviews: PropTypes.arrayOf(PropTypes.shape({})),
    bodyArray: PropTypes.arrayOf(PropTypes.shape({})),
    productType: PropTypes.string,
  }).isRequired,
  siteSettings: PropTypes.shape({
    siteId: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
      .isRequired,
    baseUrl: PropTypes.string.isRequired,
    imageDomain: PropTypes.string.isRequired,
  }).isRequired,
  wvId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  toggleRoomInfo: PropTypes.func.isRequired,
  isActive: PropTypes.bool.isRequired,
  selectedSubSection: PropTypes.number,
  changeSelectedSection: PropTypes.func.isRequired,
  nonConceptAccommodation: PropTypes.shape({
    roomTypes: PropTypes.arrayOf(PropTypes.shape({})),
  }),
};

SectionContentContainer.defaultProps = {
  selectedSubSection: 0,
  nonConceptAccommodation: null,
};

export default SectionContentContainer;
